import { listsContract } from "@openrift/shared/contracts/lists";
import { publicListsContract } from "@openrift/shared/contracts/public-lists";
import type {
  ListDetailResponse,
  ListIntent,
  ListListResponse,
  PublicListDetailResponse,
} from "@openrift/shared/types/api/list";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { listsKeys } from "@/features/lists/lib/lists-query-keys";
import { orNotFound } from "@/lib/server-fns/api-error";
import { withCookies } from "@/lib/server-fns/middleware";
import { apiOrpcClient } from "@/lib/server-fns/orpc-client";

const fetchLists = createServerFn({ method: "GET" })
  .validator((input: { intent?: ListIntent } | undefined) => input)
  .middleware([withCookies])
  .handler(({ context, data }): Promise<ListListResponse> =>
    apiOrpcClient(listsContract, context.cookie).list(data?.intent ? { intent: data.intent } : {}),
  );

const fetchListDetail = createServerFn({ method: "GET" })
  .validator((input: string) => input)
  .middleware([withCookies])
  .handler(({ context, data: listId }): Promise<ListDetailResponse> =>
    orNotFound(apiOrpcClient(listsContract, context.cookie).get({ id: listId })),
  );

const fetchPublicListFn = createServerFn({ method: "GET" })
  .validator((input: string) => input)
  .handler(({ data: token }): Promise<PublicListDetailResponse> =>
    orNotFound(apiOrpcClient(publicListsContract).share({ token })),
  );

export function listsQueryOptions(userId: string, intent?: ListIntent) {
  return queryOptions({
    queryKey: listsKeys.all(userId, intent),
    queryFn: () => fetchLists({ data: intent ? { intent } : undefined }),
    select: (data: ListListResponse) => data.items,
    staleTime: 5 * 60 * 1000,
  });
}

export function listDetailQueryOptions(userId: string, listId: string) {
  return queryOptions({
    queryKey: listsKeys.detail(userId, listId),
    queryFn: () => fetchListDetail({ data: listId }),
  });
}

export function publicListQueryOptions(token: string) {
  return queryOptions({
    queryKey: listsKeys.publicByToken(token),
    queryFn: () => fetchPublicListFn({ data: token }),
  });
}
